import { t } from 'i18next';
import { pick } from 'lodash-es';
import { create } from 'zustand';

import { authApi, userApi } from '@/api';
import { AUTH_CONST, USER_CONST } from '@/constant';
import { blobToURL, storage } from '@/package';
import type { VStorageConfig } from '@/repository';
import type { AuthApi, AuthStoreActions, AuthStoreState, User } from '@/type';

import useCognitoStore from './cognitoStore';
import useCollectionManagementStore from './collectionManagementStore';
import useTenantStore from './tenantStore';

const createUserManager = () => {
  const KEYS = AUTH_CONST.KEYS;

  const set = (newUser: User) => {
    storage.local.set(KEYS.STORAGE_USER, newUser);

    return { ...newUser };
  };

  const setToken = (
    accessToken: string,
    idToken?: string,
    refreshToken?: string,
  ) => {
    storage.local.set(KEYS.STORAGE_ACCESS_TOKEN, accessToken);
    storage.local.set(KEYS.STORAGE_ID_TOKEN, idToken);
    storage.local.set(KEYS.STORAGE_REFRESH_TOKEN, refreshToken);
  };

  const get = (): User =>
    storage.local.get(KEYS.STORAGE_USER) ?? AUTH_CONST.INIT_USER;

  const getToken = () => {
    const accessToken = storage.local.get<string>(KEYS.STORAGE_ACCESS_TOKEN);
    const refreshToken = storage.local.get<string>(KEYS.STORAGE_REFRESH_TOKEN);
    const idToken = storage.local.get<string>(KEYS.STORAGE_ID_TOKEN);

    return {
      accessToken,
      refreshToken,
      idToken,
    };
  };

  const remove = () => {
    storage.local.remove(KEYS.STORAGE_USER);

    removeToken();
  };

  const removeToken = () => {
    storage.local.remove(KEYS.STORAGE_ACCESS_TOKEN);
    storage.local.remove(KEYS.STORAGE_ID_TOKEN);
    storage.local.remove(KEYS.STORAGE_REFRESH_TOKEN);
  };

  return {
    set,
    setToken,
    get,
    getToken,
    remove,
    removeToken,
  };
};

const userManager = createUserManager();

const useAuthStore = create<AuthStoreState & AuthStoreActions>((set, get) => {
  const getCollectionManagementState = () =>
    useCollectionManagementStore.getState();
  const setStorage = (
    param: AuthApi.SignIn.Response['resultData']['storage'],
  ) => {
    const tenant = getCurrentTenant()!;
    const storageConnectionUrl = param.url;
    const config: VStorageConfig = {
      endpoint: storageConnectionUrl,
      idToken: userManager.getToken().idToken!,
      tenant: {
        id: tenant.tenantID,
        tier: tenant.tier,
      },
    };
    let bucket = '';
    const {
      currentBucket,
      connectStorage,
      setCurrentBucket,
      setStorageAccessibleBucketList,
    } = getCollectionManagementState();
    const bucketList = Object.keys(param.bucket);

    // NOTE: Minio 에서 bucket 목록이 넘어오지 않아 예외가 발생하는 경우가 있어서 기본 값을 'dev' 로 처리함
    bucket = bucketList[0] ?? 'dev';

    setStorageAccessibleBucketList(bucketList);
    setCurrentBucket(
      currentBucket && bucketList.includes(currentBucket)
        ? currentBucket
        : bucket,
    );
    connectStorage(config);
  };
  const processSignInAfter = (res: AuthApi.SignIn.Response['resultData']) => {
    const user = pick(res, [
      'id',
      'createdAt',
      'deletedAt',
      'updatedAt',
      'sub',
      'email',
      'name',
      'profileImg',
      'role',
      'group',
      'isMarketingPromotion',
      'isConsentAITraining',
    ]) as User;

    set({ user: { ...userManager.set(user) } });

    setStorage(res.storage);
  };
  const processGetTokenAfter = (res: {
    accessToken: string;
    idToken: string;
    refreshToken: string;
  }) => {
    const { accessToken, refreshToken, idToken } = res;

    userManager.setToken(accessToken, idToken, refreshToken);

    return res;
  };
  const getCurrentTenant = () => useTenantStore.getState().getCurrentTenant();

  return {
    user: userManager.get(),
    changePasswordInfo: {
      email: '',
      password: '',
      marketingPromotion: false,
      consentAITraining: false,
    },

    clearChangePasswordInfo() {
      set({
        changePasswordInfo: {
          email: '',
          password: '',
          marketingPromotion: false,
          consentAITraining: false,
        },
      });
    },

    isAdmin: () =>
      get().user.role === USER_CONST.USER_ROLE.SITE_ADMIN ||
      get().user.role === USER_CONST.USER_ROLE.ADMIN,
    isCognitoAuthenticated: () => !!userManager.getToken().idToken,
    isManager: () =>
      get().user.role === USER_CONST.USER_ROLE.MANAGER || get().isAdmin(),
    isTenantAuthenticated: () =>
      !!get().user.email && !!useTenantStore.getState().currentTenantID,
    isWorker: () => get().user.role === USER_CONST.USER_ROLE.WORKER,

    async getMe() {
      if (!get().isTenantAuthenticated()) {
        return Promise.reject(t('auth.needAuthentication.dreyfusism'));
      }

      let user: User;

      try {
        user = await userApi
          .getDetail({ id: get().user?.id })
          .then(res => res.data.resultData);
      } catch (err) {
        console.warn(err);
        get().signOut();

        throw err;
      }

      if (user.profileImg) {
        try {
          const profileImgBlob = await fetch(user.profileImg).then(res =>
            res.blob(),
          );

          user.profileImg = blobToURL(profileImgBlob);
        } catch {
          delete user.profileImg;
        }
      }

      set({ user: userManager.set(user) });

      return user;
    },
    getTokenInLocalStorage() {
      return userManager.getToken();
    },
    getUserDisplayName() {
      const { user } = get();
      const displayName = user.name || user.email?.split('@')?.[0] || user.id;

      return displayName;
    },

    refresh() {
      const { accessToken, idToken, refreshToken } = userManager.getToken();

      if (!accessToken || !idToken || !refreshToken) {
        return Promise.reject(t('auth.needAuthentication.dreyfusism'));
      }

      return useCognitoStore
        .getState()
        .refreshToken({ idToken, refreshToken })
        .then(processGetTokenAfter)
        .catch(err => {
          get().signOut();

          throw err;
        });
    },

    signIn({ email, password, newPassword }) {
      return useCognitoStore
        .getState()
        .authenticateAccount({ email, password, newPassword })
        .then(({ ...res }) => {
          set({ user: { id: '-2' } as User });

          processGetTokenAfter(res);
        });
    },
    signInByProvider(code) {
      return useCognitoStore
        .getState()
        .getTokenFromCode(code)
        .then(res => {
          set({ user: { id: '-2' } as User });

          processGetTokenAfter(res);
        });
    },
    signInTenant() {
      const idToken = get().getTokenInLocalStorage().idToken;
      const tenantId = useTenantStore.getState().currentTenantID;

      if (!idToken) {
        return Promise.reject(new Error('Please authenticate'));
      }

      if (!tenantId) {
        return Promise.reject(new Error('Tenant id is empty'));
      }

      return authApi
        .signIn()
        .then(res => res.data.resultData)
        .then(processSignInAfter);
    },
    signOut() {
      userManager.remove();

      set({ user: userManager.get() });

      useTenantStore.getState().clear();
      useCognitoStore.getState().clear();
    },
    signOutTenant() {
      userManager.remove();

      set({ user: userManager.get() });

      useTenantStore.getState().clear();
    },
    setChangePasswordInfo({
      email,
      password,
      marketingPromotion,
      consentAITraining,
    }) {
      set({
        changePasswordInfo: {
          email,
          password,
          marketingPromotion,
          consentAITraining,
        },
      });
    },
    setPromotionAgree(agree) {
      const nextUser = { ...get().user, isMarketingPromotion: agree };
      set({ user: userManager.set(nextUser) });
    },

    validatePassword(password) {
      return /^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(password);
    },
  };
});

export default useAuthStore;
